import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { PrimaryButton, SecondaryButton } from 'components/Button';
import { ProposalQuery } from 'generated/graphql';
import { formatCents } from 'utilities/money';

type ProposalOptionFromQuery = ProposalQuery['proposal']['proposalOptions'][0];

interface ProposalConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  option: ProposalOptionFromQuery | null;
  selectedLineItems: Record<string, boolean>;
  isProcessing: boolean;
}

const ProposalConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  option,
  selectedLineItems,
  isProcessing,
}: ProposalConfirmationModalProps) => {
  if (!isOpen || !option) return null;

  // Calculate selected line items and their total cost
  const selectedItems: Array<{
    hotelName: string;
    items: Array<{
      id: string;
      name: string;
      price: number;
      description?: string | null;
    }>;
  }> = [];

  let additionalCost = 0;

  option.hotels.forEach((hotel) => {
    const hotelItems = hotel.lineItems.filter(
      (item) => selectedLineItems[item.id],
    );
    if (hotelItems.length > 0) {
      const hotelName = 'name' in hotel.hotel ? hotel.hotel.name : 'Hotel';
      selectedItems.push({
        hotelName,
        items: hotelItems,
      });
      hotelItems.forEach((item) => {
        additionalCost += item.price;
      });
    }
  });

  const hasSelectedItems = selectedItems.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        {/* Background overlay */}
        <button
          type="button"
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
            }
          }}
          aria-label="Close modal"
        />

        {/* Modal panel */}
        <div className="relative overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Confirm Your Selection
              </h3>
              <button
                type="button"
                className="ml-auto rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
                disabled={isProcessing}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="size-6" aria-hidden="true" />
              </button>
            </div>

            {/* Content */}
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Please review your additional items before confirming your
                selection.
              </p>

              {/* Selected Line Items */}
              {hasSelectedItems ? (
                <div className="mt-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Selected Additional Items:
                  </h4>
                  {selectedItems.map((hotel, index) => (
                    <div key={index} className="rounded-lg bg-gray-50 p-3">
                      <h5 className="text-xs font-medium uppercase tracking-wide text-gray-700">
                        {hotel.hotelName}
                      </h5>
                      <ul className="mt-2 space-y-2">
                        {hotel.items.map((item) => (
                          <li
                            key={item.id}
                            className="flex items-start justify-between text-sm"
                          >
                            <div className="flex-1">
                              <span className="font-medium text-gray-900">
                                {item.name}
                              </span>
                              {item.description && (
                                <p className="text-xs text-gray-600">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            <span className="ml-4 font-medium text-gray-900">
                              {formatCents(item.price)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="text-sm font-medium text-gray-900">
                      Additional Items Total:
                    </span>
                    <span className="text-base font-bold text-gray-900">
                      {formatCents(additionalCost)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mt-4 rounded-lg bg-gray-50 p-3">
                  <p className="text-sm text-gray-600">
                    No additional items selected.
                  </p>
                </div>
              )}

              {/* Warning */}
              <div className="mt-6 rounded-lg bg-amber-50 p-4">
                <div className="flex">
                  <div className="shrink-0">
                    <ExclamationTriangleIcon
                      className="size-5 text-amber-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Important
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p>
                        Once you confirm this selection, you will not be able
                        to:
                      </p>
                      <ul className="mt-1 list-inside list-disc">
                        <li>Change to a different hotel option</li>
                        <li>Modify your additional item selections</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <PrimaryButton
              onClick={onConfirm}
              disabled={isProcessing}
              className="w-full sm:ml-3 sm:w-auto"
            >
              {isProcessing ? 'Processing...' : 'Confirm Selection'}
            </PrimaryButton>
            <SecondaryButton
              onClick={onClose}
              disabled={isProcessing}
              className="mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Go Back
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalConfirmationModal;
